import { ReactNode } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import TextField, { TextFieldProps } from '@mui/material/TextField'
import { Box, Tooltip } from '@mui/material'

import Iconify from '@src/components/iconify'

// ----------------------------------------------------------------------

type Props = TextFieldProps & {
  name: string
  tooltip?: ReactNode
  labelAlign?: 'left' | 'top' | 'inline'
  errShowType?: 'tooltip' | 'text'
  clearable?: boolean
  onClear?: () => void
}

export default function RHFTextField({
  name,
  helperText,
  type,
  label,
  labelAlign,
  tooltip,
  errShowType = 'text',
  clearable,
  onClear,
  ...other
}: Props) {
  const { control } = useFormContext()
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <>
          {labelAlign === 'top' ? (
            <Box display='flex' alignItems='center' mb={-1}>
              {label}
              {tooltip ? (
                <Tooltip title={tooltip} placement='top' arrow>
                  <Iconify
                    icon='entypo:help-with-circle'
                    sx={{ ml: 2, color: '#637381' }}
                    width={16}
                    height={16}
                  />
                </Tooltip>
              ) : null}
            </Box>
          ) : null}

          <Tooltip
            title={errShowType === 'tooltip' && error ? error.message : ''}
            placement='bottom'
            open
            arrow
          >
            <TextField
              {...field}
              label={labelAlign === 'top' ? '' : label}
              fullWidth
              type={type}
              value={type === 'number' && field.value === 0 ? '' : field.value}
              onChange={(event) => {
                if (type === 'number') {
                  field.onChange(Number(event.target.value))
                } else {
                  field.onChange(event.target.value)
                }
              }}
              error={!!error}
              helperText={errShowType === 'text' ? error?.message : helperText}
              {...other}
              InputProps={{
                ...other.InputProps,
                startAdornment: other.InputProps?.startAdornment,
                endAdornment: (
                  <>
                    {clearable && field.value ? (
                      <Iconify
                        icon='mdi:close-circle'
                        onClick={() => {
                          if (other.disabled) return
                          field.onChange('')
                          if (onClear) onClear()
                        }}
                        color='text.disabled'
                        style={{ cursor: other.disabled ? '' : 'pointer' }}
                      />
                    ) : null}
                    {other.InputProps?.endAdornment}
                  </>
                ),
              }}
            />
          </Tooltip>
        </>
      )}
    />
  )
}
